import { useQuery } from '@tanstack/react-query'
import { Tag, Tooltip } from 'antd'
import orderApi from 'api/orderApi'
import resourceApi from 'api/resourceApi'
import customDataTableStyle from 'assets/styles/customDataTableStyle'
import dayjs from 'dayjs'
import ContentContainer from 'general/components/ContentContainer'
import CustomDataTable from 'general/components/CustomDataTable'
import CustomSegmented from 'general/components/CustomSegmented'
import CustomTag from 'general/components/CustomTag'
import ModalUnauthorized from 'general/components/Modal/ModalUnauthorized'
import SelectOptions from 'general/constants/SelectOptions'
import Utils, { padNumber } from 'general/utils/Utils'
import { useAppSelector } from 'hooks/useRedux'
import find from 'lodash/find'
import { useCallback, useMemo, useState } from 'react'
import { shallowEqual } from 'react-redux'
import ModalInfoOrder from './components/ModalInfoOrder'
import ResourcePlans from './components/ResourcePlans'
import { renderPaymentMethod, renderPaymentStatus } from './helpers'
import './style.scss'
import {
  ORDER_PACKAGE_TYPE,
  PACKAGE_TYPE,
  RESOURCE_TYPE,
} from 'general/constants/AppConstants'
import { BuyResourceModal } from './components/BuyResourceModal'
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons'

const tabs = ['Lịch sử cấp tài nguyên', 'Quản lý đơn hàng']

function ResourceManagement() {
  const [currentTab, setCurrentTab] = useState(tabs[0])
  const userFunction = useAppSelector(
    (states) => states?.function?.userFunctions,
  )
  const listFunction = useAppSelector(
    (states) => states?.function?.listFunctions,
  )

  const systemType = useAppSelector((s) => s.auth.activeInvoice, shallowEqual)
  const [infoModalState, setInfoModalState] = useState(false)
  const [showNoRightModal, setShowNoRightModal] = useState(false)
  const [activeRow, setActiveRow] = useState(null)
  const [buyResourceModal, setBuyResourceModal] = useState(false)
  // ================== Queries n mutations ==================

  const resourceQuery = useQuery({
    enabled: currentTab === tabs[0] && systemType.invoice,
    queryKey: ['resource', systemType.invoice],
    queryFn: () =>
      resourceApi.getByCompanyId({
        invoiceType: systemType.invoice ? 'INVOICE' : 'TICKET',
      }),
  })

  // const orderQuery = useQuery({
  //   enabled: currentTab === tabs[1],
  //   queryKey: ['order'],
  //   queryFn: () => orderApi.getByCompanyId(),
  // })

  const orderQuery = useQuery({
    enabled: currentTab === tabs[1],
    queryKey: ['order', systemType.invoice],
    queryFn: () =>
      resourceApi.doiSoat({
        invoiceType: systemType.invoice ? 'INVOICE' : 'TICKET',
      }),
    select: (data) => data?.resourceHistories || [],
  })

  //==================== Memos ============================

  const [isViewRight] = useMemo(() => {
    return Utils.checkFunction(
      [
        {
          listFunctionCode: 'INFO_RESOURCES_VIEW_LIST',
        },
        {
          listFunctionCode: 'INFO_RESOURCES_EDIT',
        },
      ],
      userFunction,
      listFunction,
    )
  }, [userFunction, listFunction])

  const columns = useMemo(() => {
    /**@type {import('react-data-table-component').TableColumn<ReturnOrderData>[]} */
    const orderData = [
      {
        id: 'stt',
        center: true,
        name: 'STT',
        selector: (_row, index) => index + 1,
      },
      {
        id: 'orderCode',
        name: 'Mã đơn hàng',
        format: ({ resourceId }) => {
          return resourceId ? 'ADD' + padNumber(resourceId, 6) : ''
        },
        selector: ({ orderCode }) => orderCode,
      },
      {
        id: 'dateOrder',
        name: 'Ngày đặt hàng',

        format: ({ dateOrder }) =>
          dayjs(dateOrder).isValid() && dayjs(dateOrder).format('L'),
        selector: ({ dateOrder }) => dateOrder,
      },
      {
        id: 'packageName',
        name: 'Mã gói',
        selector: ({ packageName }) => packageName,
      },
      {
        id: 'createdAt',
        name: 'Gói dịch vụ',
        center: true,
        format: ({ invoiceType, packageType }) => {
          if (packageType === PACKAGE_TYPE.DURATION) {
            return 'Truyền nhận hoá đơn điện tử'
          }
          switch (invoiceType) {
            case RESOURCE_TYPE.INVOICE:
              return 'Hóa đơn điện tử - Hoá đơn, PXK'
            case RESOURCE_TYPE.TICKET:
              return 'Hoá đơn điện tử - Vé điện tử'
            case RESOURCE_TYPE.PERSONAL_INCOME:
              return 'Hoá đơn điện tử - Chứng từ TNCN'
            default:
              return 'Truyền nhận hoá đơn điện tử'
          }
        },
        selector: ({ invoiceType }) => invoiceType,
      },
      {
        id: 'purchasedQuantity',
        name: 'Tổng tài nguyên',
        right: true,

        selector: ({ purchasedQuantity }) => purchasedQuantity,
      },
      {
        id: 'packageCost',
        name: 'Số tiền ',
        right: true,

        format: ({ orderItem }) => {
          const totalPayment = JSON.parse(orderItem || '{}')?.packageCost || 0
          return Utils.formatNumber(totalPayment)
        },
        selector: ({ totalPayment }) => totalPayment,
      },
      {
        id: 'paymentStatus',
        name: 'Trạng thái thanh toán',
        minWidth: '200px',
        format: ({ paymentStatus }) => (
          <CustomTag
            text={renderPaymentStatus(paymentStatus).text}
            tagProps={{
              className: Utils.cn(
                'text-capitalize badge',
                renderPaymentStatus(paymentStatus).cn,
              ),
            }}
          />
        ),
        selector: ({ paymentStatus }) => renderPaymentStatus(paymentStatus),
      },
      {
        id: 'paymentMethod',
        name: 'Phương thức thanh toán',

        // format: ({ paymentMethod }) => (
        //   <Tooltip title={renderPaymentMethod(paymentMethod)}>
        //     {renderPaymentMethod(paymentMethod)}
        //   </Tooltip>
        // ),
        selector: () => 'TM/Chuyển khoản', // Tạm thời fix cố định vì chưa có hình thức khác,
      },

      {
        id: 'resourceIssuedStatus',
        name: 'Trạng thái cấp tài nguyên',
        selector: ({ active }) => {
          if (!active) {
            return (
              <Tag
                icon={<CloseCircleOutlined />}
                style={{ borderRadius: '12px' }}
                color="error"
              >
                <span style={{ fontSize: '1rem', fontWeight: 400 }}>
                  Đã huỷ
                </span>
              </Tag>
            )
          }
          return (
            <Tag
              icon={<CheckCircleOutlined />}
              style={{ borderRadius: '12px' }}
              color="success"
            >
              <span style={{ fontSize: '1rem', fontWeight: 400 }}>
                Hoàn thành
              </span>
            </Tag>
          )
        },
      },
    ]
    /** @type {import('react-data-table-component').TableColumn<ReturnResourceData>[]} */
    const resourceData = [
      {
        id: 'stt',
        center: true,
        name: 'STT',
        selector: (_row, index) => index + 1,
      },
      {
        id: 'createdAt',
        name: 'Ngày cấp',
        center: true,

        format: ({ createdAt }) =>
          dayjs(createdAt).isValid() && dayjs(createdAt).format('L'),
        selector: ({ createdAt }) => createdAt,
      },
      {
        id: 'packageName',
        name: 'Mã gói',
        left: true,
        // format: ({ packageName }) =>
        //   dayjs(packageName).isValid() && dayjs(packageName).format('L'),
        selector: ({ packageName }) => packageName,
      },
      {
        id: 'invoiceType',
        name: 'Gói dịch vụ',
        center: true,
        format: ({ invoiceType, packageType }) => {
          if (packageType === PACKAGE_TYPE.DURATION) {
            return 'Truyền nhận hoá đơn điện tử'
          }
          switch (invoiceType) {
            case RESOURCE_TYPE.INVOICE:
              return 'Hóa đơn điện tử - Hoá đơn, PXK'
            case RESOURCE_TYPE.TICKET:
              return 'Hoá đơn điện tử - Vé điện tử'
            case RESOURCE_TYPE.PERSONAL_INCOME:
              return 'Hoá đơn điện tử - Chứng từ TNCN'
            default:
              return 'Truyền nhận hoá đơn điện tử'
          }
        },
        selector: ({ invoiceType }) => invoiceType,
      },
      {
        id: 'packageType',
        name: 'Loại gói',
        center: true,

        selector: ({ packageType }) =>
          find(SelectOptions.packageType, { value: packageType })?.label,
      },
      {
        id: 'purchasedQuantity',
        name: 'Tổng tài nguyên',
        right: true,

        selector: ({ purchasedQuantity }) => purchasedQuantity,
      },
      {
        id: 'orderPackageType',
        name: 'Hình thức',
        right: true,
        format: ({ orderItem }) => {
          const orderPackageType = JSON.parse(
            orderItem || '{}',
          )?.orderPackageType
          return ORDER_PACKAGE_TYPE[orderPackageType] || 'Free'
        },
        selector: ({ orderPackageType }) => {
          return ORDER_PACKAGE_TYPE[orderPackageType]
        },
      },
    ]
    return currentTab === tabs[0] ? resourceData : orderData
  }, [currentTab])

  //==================== function ============================

  const viewDetailOrder = useCallback(
    (row) => {
      if (!isViewRight) {
        setShowNoRightModal(true)
      } else {
        setActiveRow(row)
        setInfoModalState(true)
      }
    },
    [isViewRight],
  )

  return (
    <ContentContainer wrapperClassName="ResourceManagement">
      <ContentContainer.Header titleContent={'Quản lý tài nguyên'} />
      <ContentContainer.Body className={'gap-3'}>
        <ResourcePlans setBuyResourceModal={setBuyResourceModal} />
        <div className="w-50">
          <CustomSegmented
            options={tabs}
            defaultValue={currentTab}
            onChange={(tab) => setCurrentTab(tab)}
            fullWidth
          />
        </div>
        <CustomDataTable
          columns={columns}
          scrollY="400px"
          dataSource={
            currentTab === tabs[0] ? resourceQuery.data : orderQuery.data
          }
          isLoading={
            currentTab === tabs[0]
              ? resourceQuery.isLoading
              : orderQuery.isLoading
          }
          isClickable={currentTab === tabs[1]}
          customTableStyle={customDataTableStyle}
          handleDoubleClickRow={viewDetailOrder}
        />
        {/* <OrderManagement
          updateResource={updateResource}
          setUpdateResource={setUpdateResource}
          data={orders}
          userFunction={userFunction}
          listFunction={listFunction}
          viewRight={isViewRight}
          editRight={isEditRight}
        /> */}
      </ContentContainer.Body>
      {/* Modals */}
      <ModalInfoOrder
        rowData={activeRow}
        isOpen={infoModalState}
        onOpenChange={setInfoModalState}
      />
      <ModalUnauthorized
        show={showNoRightModal}
        setShow={(status) => setShowNoRightModal(status)}
      />
      <BuyResourceModal
        show={buyResourceModal}
        setShow={(status) => setBuyResourceModal(status)}
      />
    </ContentContainer>
  )
}

export default ResourceManagement
